import type { Adjustment, MaterialInfo, PartDefinition } from '@rogue/rackbuilder'
import type { RackOptions } from '../../../RackTypes'
import { getRackModel } from '../../../modelPaths'
import { MaterialBuilder } from './materialBuilder'

/**
 * Handles back spanning crossmember (nameplate area)
 * - Nameplate
 * - Rear crossmembers (with gussets)
 * - Back stabilizer
 * 
 * Attaches between back uprights
 */
export class BackCrossmemberBuilder {
  constructor(
    private config: RackOptions,
    private materialBuilder: MaterialBuilder
  ) {}
  
  /**
   * Creates the back, top crossmember (nameplate, crossmember, or pullup bar)
   * Attaches to left back upright, spans to right back upright
   */
  // The overrides exist because Hybrid builds several back positions off one shared config; every other caller leaves them undefined and gets this.config's values.
  createBackCrossmember(backOverride?: RackOptions['back'], materialsOverride?: MaterialInfo, widthOverride?: string): PartDefinition | undefined {
    const backSelection = backOverride ?? this.config.back

    if (!backSelection) return undefined

    const isNameplate   = backSelection === 'nameplate'
    const isCrossmember = backSelection.includes('crossmember')
    const is70Width     = isCrossmember && widthOverride === '70"'

    const modelType = isNameplate ? 'NAMEPLATE' : isCrossmember ? 'CROSSMEMBERS' : 'PULLUP'

    let materials
    if (this.config.isAddon && isNameplate) {
      materials = this.createNameplateAddonMaterials()
    } else if (isNameplate) {
      materials = this.materialBuilder.createNameplateMaterials(materialsOverride, backSelection)
    } else if (this.config.isAddon) {
      materials = this.materialBuilder.createAddonMaterials()
    } else if (is70Width && this.config.line === 'RM') {
      materials = this.materialBuilder.createWideCrossmemberMaterials()
    } else {
      materials = this.materialBuilder.createBackCrossmemberMaterials(backSelection, materialsOverride)
    }

    const adjustments: Adjustment[] = isCrossmember ? [{ type: 'rotate', axis: 'Y', amount: 90 }] : []

    // The 70" crossmember is modeled facing the opposite way from the standard crossmember meshes. Cave corrects its copy with a Z flip, but a flip is a mirror and reverses the branding, and with the span and up axis both fixed no pure rotation can turn the facing alone. Anchoring the far end instead lets a real 180 do it - 'front' spans back the other way, so the extra turn lands it the same place the right way round.
    if (is70Width) adjustments.push({ type: 'rotate', axis: 'Y', amount: 180 })

    // 70"-wide units reuse the same "crossmember top 7" part the 70" depth crossmember uses (no standard-width nameplate/crossmember mesh spans that far).
    const part: PartDefinition = {
      // Only the Hybrid reaches this branch - PowerRackBuilder's crossmemberWidth is undefined and the rig is the one builder that overrides it - so the key always resolves to the rig's own chunk.
      mesh: is70Width
        ? getRackModel(`${this.config.line}_HYBRID_LARGE`, 'crossmember top 7')
        : getRackModel(`${this.config.line}_${modelType}`, backSelection),
      // The ML beam's 'front_top' anchor is authored for the 'top' slot, which sits on the side ladder 1" above the 'crossmember-top' hole 'front' is made for.
      anchor  : is70Width ? (this.config.line === 'RML' ? 'front_top' : 'front') : (isCrossmember ? 'snowman_bottom' : 'left'),
      adjustments,
      slots   : {},
      materials,
      effects : this.config.isAddon && (!isNameplate || this.config.line === 'RML') ? { opacity: 0.2 } : {},
    }

    this.applyBackCrossmemberAdjustments(part, backSelection)

    return part
  }

  private createNameplateAddonMaterials() {
    return {
      main: [
        { name: 'unpainted', color: 'white', config: { opacity: 0.01 } },
        { name: 'metal', color: '#d52716', config: { opacity: 0.1 } },
      ],
      base     : [{ color: 'black', config: { opacity: 0.4 } }],
      hardware : { config: { opacity: 0.2 } },
    }
  }
  
  private applyBackCrossmemberAdjustments(part: PartDefinition, backSelection: RackOptions['back']): void {
    // Fat/skinny rear crossmember needs offset
    if (backSelection?.includes('skinny')) {
      part.offset = { x: 0.1 }
    }

    // 80" height adjustment for crossmember
    if (backSelection?.includes('crossmember') && this.config.height === '80' && this.config.line !== 'RM') {
      part.offset = { y: 1.5 }
    }
  }
  
  // Creates back stabilizer (connects back uprights at bottom)
  createBackStabilizer(): PartDefinition | undefined {
    if (!this.config.backStabilizer) return undefined
    
    const materials = this.materialBuilder.createBackStabilizerMaterials()
    
    if (this.config.line === 'RML') {
      return {
        mesh        : getRackModel('RML_CROSSMEMBERS', 'back_stabilizer'),
        slots       : {},
        anchor      : '',
        adjustments : [{ type: 'rotate', axis: 'X', amount: 90 }],
        offset      : { y: -3, x: 0, z: 0 },
        materials   : this.config.isAddon ? this.materialBuilder.createAddonMaterials() : materials,
      }
    }
    
    // RM back stabilizer (uses crossmember mesh)
    return {
      mesh        : getRackModel('RM_CROSSMEMBERS', 'crossmember_bottom_4'),
      slots       : {},
      anchor      : 'back',
      adjustments : [{ type: 'rotate', axis: 'Y', amount: 90 }],
      offset      : { y: -6, x: 0, z: 0 },
      materials   : this.config.isAddon ? this.materialBuilder.createAddonMaterials() : materials,
    }
  }
}